import { Stack } from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import type { Bedrijf, Contactpersoon, VacatureDetails } from '../../api/typen.ts';
import { useVacatureMutaties } from '../../api/vacatures.ts';
import { FormulierDialoog } from '../../componenten/FormulierDialoog.tsx';
import { BedrijfVeld, ContactpersoonVeld } from '../../componenten/keuzevelden.tsx';
import { useVerzenden } from '../../hooks/useVerzenden.ts';

interface Waarden {
  bedrijf: Bedrijf | null;
  contactpersoon: Contactpersoon | null;
}

/**
 * Koppelt een vacature aan een bedrijf, optioneel met een contactpersoon van dat bedrijf.
 * @param props De vacature.
 * @returns De dialoog.
 */
export function KlantKoppelingFormulier({
  open,
  onSluit,
  vacature,
}: {
  open: boolean;
  onSluit: () => void;
  vacature: VacatureDetails;
}) {
  const mutaties = useVacatureMutaties();
  const formulier = useForm<Waarden>({
    defaultValues: { bedrijf: vacature.bedrijf, contactpersoon: vacature.contactpersoon },
  });
  useEffect(() => {
    if (open) {
      formulier.reset({ bedrijf: vacature.bedrijf, contactpersoon: vacature.contactpersoon });
    }
  }, [open, vacature, formulier]);

  const bedrijf = formulier.watch('bedrijf');
  const contactpersoon = formulier.watch('contactpersoon');
  useEffect(() => {
    if (contactpersoon && contactpersoon.bedrijfId !== bedrijf?.id) {
      formulier.setValue('contactpersoon', null);
    }
  }, [bedrijf, contactpersoon, formulier]);

  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    (waarden) => {
      if (!waarden.bedrijf) {
        throw new Error('Kies een bedrijf.');
      }
      return mutaties.koppelBedrijf.mutateAsync({
        id: vacature.id,
        invoer: { bedrijfId: waarden.bedrijf.id, contactpersoonId: waarden.contactpersoon?.id ?? null },
      });
    },
    { melding: 'Klant gekoppeld', naSucces: onSluit },
  );

  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel="Klant koppelen"
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
      >
        <Stack spacing={2}>
          <BedrijfVeld naam="bedrijf" verplicht autoFocus />
          <ContactpersoonVeld naam="contactpersoon" bedrijfId={bedrijf?.id} uitgeschakeld={!bedrijf} />
        </Stack>
      </FormulierDialoog>
    </FormProvider>
  );
}
